import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../lib/api";
import { useSeo } from "../lib/seo";

export default function ContactPage() {
  useSeo({
    title: "Contact Us",
    description:
      "Questions about children's clothing, sizes or your order? Message the Sparkwear team — a real parent replies within 1 working day.",
  });
  const [form, setForm] = useState({ name: "", email: "", order_number: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we usually reply within 1 working day 💚");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="mx-auto min-h-[70vh] w-full max-w-2xl px-5 py-14 sm:px-8">
      <h1 className="font-display text-4xl font-semibold text-ink sm:text-5xl">Say hello 👋</h1>
      <p className="mt-3 text-muted">
        Questions about sizes, an order, or a little one's wishlist? Write to us — a real parent answers, usually within 1 working day.
      </p>

      {sent ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          data-testid="contact-success"
          className="mt-10 rounded-[28px] border border-mint-100 bg-white p-10 text-center"
        >
          <CheckCircle2 className="mx-auto h-12 w-12 text-mint-600" />
          <p className="mt-4 font-display text-2xl font-semibold text-ink">Message sent!</p>
          <p className="mt-2 text-sm text-muted">Thank you, {form.name.split(" ")[0] || "friend"} — we'll reply to {form.email} shortly.</p>
          <button
            onClick={() => { setSent(false); setForm({ name: "", email: "", order_number: "", message: "", company: "" }); }}
            data-testid="contact-send-another"
            className="mt-6 rounded-full border border-lav-100 px-6 py-3 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-blush-50"
          >
            Send another message
          </button>
        </motion.div>
      ) : (
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="mt-10 rounded-[28px] border border-lav-100 bg-white p-6 sm:p-8"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Your name *</span>
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Mama Ade"
                data-testid="contact-name-input"
                className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Your email *</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.com"
                data-testid="contact-email-input"
                className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
              />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Order number (optional)</span>
            <input
              value={form.order_number}
              onChange={set("order_number")}
              placeholder="SPK-1F8D18F6 — helps us find your order fast"
              data-testid="contact-order-input"
              className="mt-2 w-full rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
            />
          </label>

          <label className="mt-4 block">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-muted">Your message *</span>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="Hello Sparkwear! I'd love to know..."
              data-testid="contact-message-input"
              className="mt-2 w-full resize-none rounded-2xl border border-lav-100 bg-cream px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-blush-500"
            />
          </label>

          <input
            type="hidden"
            value={form.company}
            onChange={set("company")}
            data-testid="contact-honeypot"
            tabIndex={-1}
            autoComplete="off"
          />

          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-display text-base font-semibold text-white transition-transform duration-300 hover:scale-[1.02] disabled:opacity-70"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? "Sending..." : "Send message"}
          </button>
          <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-muted">
            <Mail className="h-3.5 w-3.5" />
            Prefer email? Write to us at sparkspacefind@gmail.com
          </p>
        </form>
      )}
    </main>
  );
}
